import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { AlertTriangle, Clock3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPower } from '@/lib/inventory'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import CmsText from '@/components/datacenter/cms-text'

import {
    CountUp,
    LogoMark,
    Meter,
    glass
} from '@/components/datacenter/hud/primitives'

import { siteConfig } from '@/constants/site'

const LiveClock: FC = (): ReactNode => {
    const [now, setNow] = useState<Date | null>(null)

    useEffect(() => {
        setNow(new Date())

        const timer = window.setInterval(() => setNow(new Date()), 1000)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <span className='font-mono tabular-nums'>
            {now ? now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--'}
        </span>
    )
}

const TopBar: FC = (): ReactNode => {
    const { inventory, stats } = useInventory()
    const setInventoryOpen = useDatacenterStore(state => state.setInventoryOpen)
    const setStatusFilter = useDatacenterStore(state => state.setStatusFilter)
    const setCategoryFilter = useDatacenterStore(state => state.setCategoryFilter)
    const setSearch = useDatacenterStore(state => state.setSearch)
    const { facility } = inventory
    const loadColor = stats.capacityKw && stats.powerKw / stats.capacityKw > 0.8 ? '#fb923c' : '#22d3ee'

    const openAlerts = () => {
        setSearch('')
        setCategoryFilter('all')
        setStatusFilter(stats.byStatus.critical ? 'critical' : 'warning')
        setInventoryOpen(true)
    }

    return (
        <motion.header
            className='pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-3 p-3 sm:p-4'
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className={cn(glass, 'pointer-events-auto flex min-w-0 items-center gap-3 px-3 py-2.5 sm:px-4')}>
                <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300 ring-1 ring-cyan-300/25'>
                    <LogoMark className='size-5' />
                </span>
                <div className='min-w-0'>
                    <p className='font-heading text-sm font-semibold tracking-tight text-white'>
                        {siteConfig.name}
                    </p>
                    {facility && (
                        <p className='flex min-w-0 items-center gap-1.5 text-xs text-slate-400'>
                            <CmsText table='facilities' id={facility.id} field='code' value={facility.code} className='font-mono text-slate-300' />
                            {facility.location && (
                                <>
                                    <span aria-hidden='true'>·</span>
                                    <CmsText table='facilities' id={facility.id} field='location' value={facility.location} className='hidden truncate sm:inline' />
                                </>
                            )}
                        </p>
                    )}
                </div>
            </div>

            <div className={cn(glass, 'pointer-events-auto hidden items-stretch divide-x divide-white/10 md:flex')}>
                <div className='px-4 py-2.5'>
                    <p className='text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500'>
                        Racks
                    </p>
                    <CountUp value={stats.racks} className='font-heading text-lg font-semibold text-white' />
                </div>
                <div className='px-4 py-2.5'>
                    <p className='text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500'>
                        Devices
                    </p>
                    <CountUp value={stats.devices} className='font-heading text-lg font-semibold text-white' />
                </div>
                <div className='w-44 px-4 py-2.5'>
                    <p className='flex items-baseline justify-between text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500'>
                        <span>
                            IT load
                        </span>
                        <span className='normal-case tracking-normal text-slate-400'>
                            of {formatPower(stats.capacityKw)}
                        </span>
                    </p>
                    <CountUp value={stats.powerKw} format={formatPower} className='font-heading text-lg font-semibold text-white' />
                    <Meter value={stats.powerKw} max={stats.capacityKw} color={loadColor} className='mt-1' />
                </div>
                <button
                    type='button'
                    onClick={openAlerts}
                    disabled={!stats.alerts}
                    className='group px-4 py-2.5 text-left transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-300 disabled:cursor-default disabled:hover:bg-transparent'
                    aria-label={`${stats.alerts} devices need attention, show them in the inventory`}
                >
                    <p className='text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500'>
                        Alerts
                    </p>
                    <p className='flex items-center gap-1.5'>
                        <AlertTriangle className={cn('size-4', stats.byStatus.critical ? 'animate-pulse text-rose-400' : 'text-amber-300')} aria-hidden='true' />
                        <CountUp value={stats.alerts} className='font-heading text-lg font-semibold text-white' />
                    </p>
                </button>
                <div className='hidden px-4 py-2.5 xl:block'>
                    <p className='text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500'>
                        Local time
                    </p>
                    <p className='flex items-center gap-1.5 pt-1 text-sm text-slate-200'>
                        <Clock3 className='size-3.5 text-slate-500' aria-hidden='true' />
                        <LiveClock />
                    </p>
                </div>
            </div>

            <button
                type='button'
                onClick={openAlerts}
                disabled={!stats.alerts}
                className={cn(glass, 'pointer-events-auto flex items-center gap-2 px-3 py-2.5 text-sm text-white md:hidden')}
                aria-label={`${stats.alerts} devices need attention`}
            >
                <AlertTriangle className={cn('size-4', stats.byStatus.critical ? 'text-rose-400' : 'text-amber-300')} aria-hidden='true' />
                {stats.alerts}
            </button>
        </motion.header>
    )
}

export default TopBar
